import { useContext, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { ApiError, extractErrorMessage } from "@/api/client";
import {
  impersonationApi,
  isPortalImpersonationActiveDetails,
  portalPathForAudience,
  type PortalImpersonationActiveDetails,
} from "@/api/impersonation";
import { AuthContext } from "@/hooks/authContext";

// Admin User Impersonation — the "Log in as" trigger, shared by the staff
// user registry (/admin/users) and the Portal Accounts registry.
//
// SELF-GATING: the component decides for itself whether it may render, from
// the caller's own session, so no page has to thread an `isAdmin` flag through
// its rows (the AdminUndoButton precedent). Outside an AuthProvider the
// context is undefined and the affordance simply does not render — never a
// crash.
//
// It reads `AuthContext` DIRECTLY rather than calling `useOptionalAuth`, which
// is what `@/hooks/authContext` exists for and says so on the tin: this is a
// leaf mounted by pages whose own tests `vi.mock("@/hooks/useAuth")` with a
// partial factory, and an ESM mock THROWS on any export it did not declare.
// Depending on the context instead of a sibling wrapper export means no
// consumer's module mock can break this component.

/** Permission the backend enforces; the client only decides whether to OFFER. */
export const IMPERSONATE_PERMISSION = "Users.Impersonate";

export type LogInAsTarget =
  | {
      kind: "staff";
      userId: string;
      /** Name (or email) shown in the confirmation. */
      label: string;
      /** Role codes on the target row — an ADMIN target hides the control. */
      roleCodes: string[];
    }
  | {
      kind: "portal";
      portalUserId: string;
      label: string;
      /** MaterialAudience name; picks the portal route the new tab opens. */
      accountType: string;
    };

interface Props {
  target: LogInAsTarget;
  size?: "sm" | "md";
}

export function LogInAsAction({ target, size = "sm" }: Props) {
  const auth = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The server's structured refusal of a SECOND concurrent portal session. Held
  // apart from `error` because it is not a message to read and dismiss — it
  // names a session that is still live and carries the action that clears it.
  const [conflict, setConflict] =
    useState<PortalImpersonationActiveDetails | null>(null);

  const start = useMutation({
    mutationFn: () =>
      target.kind === "staff"
        ? impersonationApi.startStaff(target.userId)
        : impersonationApi.startPortal(target.portalUserId),
    // no-invalidation: starting an impersonation swaps the session identity, so
    // there is nothing worth refetching under the old one. The staff path does
    // a FULL page reload (which discards the cache outright); the portal path
    // opens a separate tab and deliberately leaves THIS page — and its cache —
    // exactly as the admin left it.
    onSuccess: (result) => {
      if (target.kind === "staff") {
        // The staff cookie now belongs to someone else: every cached query on
        // screen was fetched as the admin, so a soft navigation would leave
        // their data visible under the impersonated identity.
        window.location.assign("/");
        return;
      }
      // The admin's own staff session is untouched — the impersonated portal
      // lives in its own tab, and exiting is scoped to that tab.
      const path = portalPathForAudience(
        result?.portalAccountType ?? target.accountType,
      );
      const opened = window.open(path, "_blank", "noopener");
      if (!opened) {
        // A BLOCKED POP-UP IS NOT A NO-OP. This call is post-`await`, so the
        // user activation that would have let it through is gone — and by the
        // time it is refused the impersonated session and its audit row already
        // exist server-side. Discarding the return value closed the modal on a
        // started session the admin could not see, so the only visible symptom
        // was nothing happening. Keep the dialog open and say what is true.
        setError(
          `Your portal session as ${result?.targetLabel ?? target.label} started, ` +
            `but this browser blocked the new tab. Allow pop-ups for this site, ` +
            `then open ${path} — the session stays live until you exit it there.`,
        );
        return;
      }
      setOpen(false);
    },
    onError: (err: unknown) => {
      // Branch on the machine-readable code, NEVER on the prose: the message is
      // copy and will be reworded, and a string match would stop recognising the
      // refusal the moment it was — silently, with the fallback still rendering
      // something plausible.
      const refusal =
        err instanceof ApiError && isPortalImpersonationActiveDetails(err.details)
          ? err.details
          : null;
      setConflict(refusal);
      setError(refusal ? null : extractErrorMessage(err));
    },
  });

  const endLivePortalSession = useMutation({
    mutationFn: () => impersonationApi.endPortal(),
    // no-invalidation: ending the OTHER tab's impersonated portal session
    // changes nothing THIS page has read — the admin's own staff session, and
    // every query cached under it, is untouched by design (same reasoning as
    // the start above).
    onSuccess: () => {
      setConflict(null);
      setError(null);
      start.mutate();
    },
    // Deliberately leaves `conflict` set: the live session is still there, so
    // the recovery button must stay on screen to be retried.
    onError: (err: unknown) => setError(extractErrorMessage(err)),
  });

  const busy = start.isPending || endLivePortalSession.isPending;

  // Gate 1 — the caller must hold the permission AND be an admin. Both, not
  // either: the permission is the capability, ADMIN is the role that may use it.
  const allowed =
    !!auth && auth.hasPermission(IMPERSONATE_PERMISSION) && auth.hasRoleCode("ADMIN");
  if (!allowed) return null;

  // Gate 2 — HIDDEN, not disabled, for an admin target. The backend refuses
  // admin-on-admin impersonation unconditionally, and a control that can only
  // ever fail is worse than no control at all.
  if (target.kind === "staff" && target.roleCodes.includes("ADMIN")) return null;

  const isStaff = target.kind === "staff";

  return (
    <>
      <Button
        variant="secondary"
        size={size}
        onClick={() => {
          setError(null);
          setConflict(null);
          setOpen(true);
        }}
      >
        <KeyRound size={14} strokeWidth={1.5} aria-hidden /> Log in as
      </Button>

      <ConfirmModal
        open={open}
        title={`Log in as ${target.label}?`}
        confirmLabel={start.isPending ? "Starting…" : "Log in as this user"}
        cancelLabel="Cancel"
        onCancel={() => {
          if (busy) return;
          setError(null);
          setConflict(null);
          setOpen(false);
        }}
        onConfirm={() => {
          if (busy) return;
          setError(null);
          setConflict(null);
          start.mutate();
        }}
        message={
          <div className="grid gap-space-2">
            {isStaff ? (
              <p className="m-0">
                Your own session is replaced until you exit impersonation. You
                will see the app exactly as {target.label} sees it.
              </p>
            ) : (
              <p className="m-0">
                The {target.accountType} portal opens in a new tab as{" "}
                {target.label}. Your staff session here is untouched — exit from
                that tab when you are done.
              </p>
            )}
            {/* This sentence is a factual claim about attribution, so it has
                to match what the write paths do. During impersonation the
                principal IS the target (ImpersonationService.cs:177 signs in
                `target`), so `ICurrentUserAccessor.UserId` returns the
                target's id and the ~737 sites that stamp an actor from it —
                CommunicationLogService.cs:102's `LoggedByUserId` among them —
                record the TARGET. Only the audit trail carries the admin, on
                its own column, where one is written at all (AuditService.cs:56
                and :64 write `UserId` = target and `ImpersonatedByUserId` =
                admin; CommissionAuditService does the same at :55 and :63).
                The second sentence deliberately claims only the SESSION, not
                the record: CommunicationLogService — the module this comment
                uses as its example — has NO IAuditService dependency at all and
                writes a CommunicationLogActivity whose only actor field is the
                target, so no audit row anywhere names the admin for an entry
                logged while impersonating. What always holds is that
                ImpersonationService writes ImpersonationStarted / …Ended rows
                carrying ImpersonatedByUserId = the admin (:565), so the session
                is on record and names them. Pinned by LogInAsAction.test.tsx. */}
            <p className="m-0 text-gray-mid">
              Anything you create is attributed to {target.label}, not to you.
              This session is recorded, and it names you.
            </p>
            {conflict && (
              <div className="grid gap-space-2">
                <p className="m-0 text-status-error" role="alert">
                  You already have a portal session open as{" "}
                  {conflict.targetLabel}. End it before starting another.
                </p>
                <div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      if (busy) return;
                      setError(null);
                      endLivePortalSession.mutate();
                    }}
                  >
                    {endLivePortalSession.isPending
                      ? "Ending…"
                      : "End that session and continue"}
                  </Button>
                </div>
              </div>
            )}
            {error && (
              <p className="m-0 text-status-error" role="alert">
                {error}
              </p>
            )}
          </div>
        }
      />
    </>
  );
}
